import { useCallback, useEffect, useRef, useState } from 'react';
import { useFilter } from './useFilter';
import { useLocation, useParams } from 'react-router-dom';
import UIHelper from '../helpers/UIHelper';
import Helpers from '../helpers';
import { useTranslation } from 'react-i18next';
import { useTableChangeFilterHook } from './useTableChangeFilterHook';
import { atom, selector, useRecoilState } from 'recoil';
import { useNavigate } from './useNavigate';

const pendingRequestState = atom({
  key: 'pendingRequestState',
  default: {
    inProgress: false,
  },
});

export const getPendingRequestState = selector({
  key: 'getPendingRequestState',
  get: ({ get }) => {
    return get(pendingRequestState);
  },
});

const defaultState = {
  pagination: {
    current: 1,
    pageSize: 30,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const aditionalParamsForSingleDefault = {};
const aditionalPaginatonParamsDefault = {};
const getInitialState = (resourceKey, aditionalPaginatonParams) => ({
  ...defaultState,
  [resourceKey]: [],
  pagination: {
    ...defaultState.pagination,
    ...aditionalPaginatonParams,
  },
});

export const useApi = ({
  resourceKey = '',
  singleResourceKey = '',
  api,
  adapter,
  isMainPage = false,
  aditionalFilters,
  aditionalPaginatonParams = aditionalPaginatonParamsDefault,
  aditionalParamsForSingle = aditionalParamsForSingleDefault,
  aditionalFunctionForSingleItemCall = () => true,
}) => {
  const navigate = useNavigate();
  const [approveErrors, setApproveErrors] = useState([]);
  const { searchParams, handleChangeFilters } = useFilter();
  const { pathname } = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();
  const onChangeTable = useTableChangeFilterHook();
  const [filtersFromBack, setFiltersFromBack] = useState({});
  const [data, setData] = useState(
    getInitialState(resourceKey, aditionalPaginatonParams),
  );
  const [, setPendingRequest] = useRecoilState(pendingRequestState);
  const [loading, setLoading] = useState(true);
  const [singleItem, setSingleItem] = useState({});
  const [updateInProcess, setUpdateInProcess] = useState(false);

  const [formErrors, setFormErrors] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchList = async () => {
    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const searchParamsObj = Object.fromEntries([...searchParams]);

    setLoading(true);
    try {
      let params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...searchParamsObj,
      };
      if (aditionalFilters) {
        params = {
          ...params,
          ...aditionalFilters,
        };
      }
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: {
          [resourceKey]: resource,
          pagination = {
            total_count: 0,
          },
          filters = {},
        },
      } = await api.list({ 
        params,
        props: {
          signal: abortController.signal,
        },
      });
      
      // Only update state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setData((s) => ({
          ...s,
          [resourceKey]: [...(adapter ? adapter(resource) : resource)],
          pagination: {
            ...defaultState.pagination,
            ...pagination,
            total: pagination?.total_count,
            ...aditionalPaginatonParams,
          },
        }));
        setFiltersFromBack(filters);
      }
    } catch (e) {
      // Ignore abort errors
      if (e.name === 'AbortError' || e.name === 'CanceledError' || (e.code === 'ERR_CANCELED')) {
        return;
      }
      console.log('e', e);
    } finally {
      // Only set loading to false if this is still the current request
      if (abortControllerRef.current === abortController) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  };

  const fetchItem = async () => {
    //this  is  one  new  but is global ***********************************
    if (aditionalFunctionForSingleItemCall(aditionalParamsForSingle)) {
      setLoading(true);
      try {
        const { data } = await api.get(id, {
          params: aditionalParamsForSingle,
        });
        let { [singleResourceKey]: resource } = data;
        resource = { ...resource, allData: data };

        setSingleItem(adapter ? adapter(resource) : resource);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isMainPage) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMainPage,
    searchParams,
    data.pagination.current,
    data.sorting.order,
    aditionalFilters,
  ]);

  // Cleanup: abort any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setPendingRequest((s) => ({
      ...s,
      inProgress: loading || updateInProcess,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateInProcess, loading]);

  useEffect(() => {
    if (!isMainPage && id) {
      fetchItem();
    }
    if (!isMainPage && !id) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isMainPage, aditionalParamsForSingle]);
  const removeDiacritics = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const response = await api.getExcel({ params });
      const fr = new FileReader();

      fr.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          if (json.success) {
            UIHelper.openNotificationWithIcon(
              'success',
              'Il file è troppo grande, verrà inviato via email.',
            );
          }
        } catch (e) {
          const currentActiveItem = document.querySelector(
            '.ant-menu-item-selected',
          ).textContent;
          let fileTitle = '';
          if (currentActiveItem.trim().split(/\s+/).length > 1) {
            fileTitle += `${removeDiacritics(currentActiveItem)
              .replace(/\s+/g, '_')
              .toLowerCase()}`;
          } else {
            fileTitle += `${removeDiacritics(currentActiveItem).toLowerCase()}`;
          }
          console.log(e);
          const currentDate = new Date();
          const formattedDate = `${currentDate.getDate()}_${(
            currentDate.getMonth() + 1
          )
            .toString()
            .padStart(2, '0')}_${currentDate
            .getFullYear()
            .toString()
            .padStart(2, '0')}`;
          Helpers.downloadFile(response, `${fileTitle}_${formattedDate}.xlsx`);
        }
      };

      fr.readAsText(response.data);
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogsDownloadExcel = async (id) => {
    setUpdateInProcess(true);
    try {
      const currentActiveItem = document.querySelector(
        '.ant-menu-item-selected',
      ).textContent;
      let fileTitle = '';
      if (currentActiveItem.trim().split(/\s+/).length > 1) {
        fileTitle += `${removeDiacritics(currentActiveItem)
          .replace(/\s+/g, '_')
          .toLowerCase()}`;
      } else {
        fileTitle += `${removeDiacritics(currentActiveItem).toLowerCase()}`;
      }
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const response = await api.getItemLogsExcel(id, { params });
      const currentDate = new Date();
      const formattedDate = `${currentDate.getDate()}_${(
        currentDate.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}_${currentDate
        .getFullYear()
        .toString()
        .padStart(2, '0')}`;
      Helpers.downloadFile(response, `${fileTitle}_${formattedDate}.xlsx`);
    } catch (e) {
      console.log(e);
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setUpdateInProcess(false);
    }
  };
  const onDelete = async (id) => {
    setUpdateInProcess(true);
    try {
      await api.destroy(id);
      navigate(UIHelper.getListRoute(pathname));
      fetchList();
    } catch (e) {
      const errors = e?.response?.data?.base || [];
      if (errors.length) {
        UIHelper.openNotificationWithIcon('error', errors.join('\n'));
      }
    } finally {
      setUpdateInProcess(false);
    }
  };

  const onUpdate = async (data) => {
    setUpdateInProcess(true);
    setPendingRequest({ inProgress: true });
    try {
      await api.put(id, { data });
      navigate(UIHelper.getListRoute(pathname));
      fetchList();
    } catch (e) {
      setFormErrors(e?.response?.data);
    } finally {
      setUpdateInProcess(false);
    }
  };

  const resetFormErrors = () => {
    if (formErrors) {
      setFormErrors(null);
    }
  };

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      comment: values.description,
    };
    switch (values.status) {
      case 'Approva': {
        if (!paramKey) {
          data.confirmed = true;
        } else {
          data[paramKey] = 'approva';
        }
        break;
      }
      case 'Rifiutato': {
        if (!paramKey) {
          data.refused = true;
        } else {
          data[paramKey] = 'respingi';
        }
        break;
      }
      case 'Escalate to admin': {
        data.to_admin = true;
        break;
      }
      case 'Annulla Intervento': {
        data.refused = true;
        break;
      }
      case 'publish': {
        data.publish = true;
        break;
      }
      case 'unpublish': {
        data.unpublish = true;
        break;
      }
      default:
        break;
    }
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      console.log(e);
      setApproveErrors(Object.values(e?.response?.data || {}));
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    data,
    loading,
    singleItem,
    onDelete,
    updateInProcess,
    setLoading,
    formErrors,
    resetFormErrors,
    onUpdate,
    handleDownloadExcel,
    handleChangeFilters,
    handleLogsDownloadExcel,
    onChangeTableFilter,
    setUpdateInProcess,
    setSingleItem,
    handleApprove,
    fetchList,
    searchParams,
    fetchItem,
    filtersFromBack,
    approveErrors,
    setApproveErrors,
  };
};
